// src/components/Login.tsx
import React, { useState, useContext } from 'react';
import {
    TextField,
    Button,
    Container,
    Typography,
    Box,
    Paper,
    CircularProgress,
} from '@mui/material';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const Login: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();
    const [errorMessage, setErrorMessage] = useState('');
    const [loading, setLoading] = useState(false); // New loading state

    const handleLogin = async (event: React.FormEvent) => {
        event.preventDefault();
        setErrorMessage('');
        setLoading(true); // Start loading
        try {
            const params = new URLSearchParams();
            params.append('username', email);
            params.append('password', password);

            const response = await api.post('/user/auth', params, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                },
            });

            const { access_token, token_type, is_api_keys_set } = response.data;

            // Store only the access token
            localStorage.setItem('accessToken', access_token);
            login();

            // Navigate based on whether API keys are set
            if (is_api_keys_set) {
                navigate('/chat');
            } else {
                navigate('/add-api-keys');
            }
        } catch (error) {
            setErrorMessage('Invalid email or password');
        } finally {
            setLoading(false); // Stop loading
        }
    };

    return (
        <Container maxWidth="sm">
            <Box mt={8} component={Paper} elevation={3} p={4} borderRadius="12px">
                <Typography
                    variant="h4"
                    gutterBottom
                    align="center"
                    sx={{
                        fontWeight: 600,
                        color: '#0D47A1', // Primary theme color for consistency
                    }}
                >
                    Welcome Back
                </Typography>
                {errorMessage && (
                    <Typography color="error" variant="body2" align="center" gutterBottom>
                        {errorMessage}
                    </Typography>
                )}
                <form onSubmit={handleLogin}>
                    <TextField
                        label="Email"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading} // Disable while loading
                        InputProps={{
                            style: {
                                borderRadius: '8px',
                            },
                        }}
                    />
                    <TextField
                        label="Password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        disabled={loading} // Disable while loading
                        InputProps={{
                            style: {
                                borderRadius: '8px',
                            },
                        }}
                    />
                    <Box mt={3}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            fullWidth
                            disabled={loading} // Disable button while loading
                            sx={{
                                fontWeight: 600,
                                padding: '0.75rem',
                                fontSize: '1rem',
                                borderRadius: '20px', // Rounded for a softer, more modern look
                                textTransform: 'none',
                                boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)', // Light shadow for depth
                            }}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Login'}
                        </Button>
                    </Box>
                </form>
            </Box>
        </Container>
    );
};

export default Login;
